import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export type ItemBarraLateral = { key: string; label: string; icon: LucideIcon; path: string };

/**
 * A barra lateral do computador — a mesma base do celular, adaptada.
 *
 * É a navbar em pé: os mesmos destinos, o mesmo ícone com rótulo, a mesma
 * pílula de ouro marcando o ativo. 250px de largura, fixa à esquerda,
 * escondida abaixo de 901px (aí a navbar flutuante assume). Quem monta a
 * lista de itens é o DesktopSidebar (app) ou o CleanCakesSidebar (namespace).
 *
 * A moldura das telas (AppLayout) reserva os mesmos 250px à esquerda
 * (.com-barra-lateral em sistema.css): os dois números vivem lá.
 */
export function BarraLateral({
  marca,
  itens,
  ativo,
  aoEscolher,
  rodape,
}: {
  /** Ícone e nome do app, no topo. */
  marca: { icone: string; nome: string };
  itens: ItemBarraLateral[];
  /** key do item ativo, ou null. */
  ativo: string | null;
  aoEscolher: (item: ItemBarraLateral) => void;
  /** Ações do pé: tema, ajustes, sair. */
  rodape?: ReactNode;
}) {
  return (
    <aside className="barra-lateral">
      <div className="flex items-center gap-3" style={{ padding: "6px 8px 22px" }}>
        <img
          src={marca.icone}
          alt=""
          className="object-cover"
          style={{ width: 44, height: 44, borderRadius: 14, boxShadow: "0 0 0 2px hsl(var(--accent))" }}
        />
        <span className="font-display font-bold leading-none" style={{ fontSize: "var(--t-item)" }}>
          {marca.nome}
        </span>
      </div>

      <nav className="flex flex-col" style={{ gap: 6 }}>
        {itens.map((item) => {
          const ehAtivo = item.key === ativo;
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => aoEscolher(item)}
              aria-current={ehAtivo ? "page" : undefined}
              className={`toque flex w-full items-center gap-3 rounded-full text-left font-semibold ${ehAtivo ? "" : "hover:bg-secondary"}`}
              style={{
                minHeight: "var(--alvo-min)",
                padding: "0 14px",
                fontSize: "var(--t-linha)",
                backgroundColor: ehAtivo ? "hsl(var(--accent))" : undefined,
                color: ehAtivo ? "hsl(var(--accent-foreground))" : "var(--tinta)",
                transition: "background-color 180ms, color 180ms, transform 140ms",
              }}
            >
              <Icon size={24} strokeWidth={ehAtivo ? 2.4 : 2} />
              {item.label}
            </button>
          );
        })}
      </nav>

      {rodape && <div className="mt-auto flex flex-col" style={{ gap: 4, paddingTop: 24 }}>{rodape}</div>}
    </aside>
  );
}

/** Uma ação do pé da barra lateral (tema, ajustes, sair). */
export function AcaoBarraLateral({
  icone: Icon,
  rotulo,
  onClick,
  cor,
}: {
  icone: LucideIcon;
  rotulo: string;
  onClick: () => void;
  cor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="toque flex w-full items-center gap-3 rounded-full text-left font-semibold hover:bg-secondary"
      style={{ minHeight: "var(--alvo-min)", padding: "0 14px", fontSize: "var(--t-apoio)", color: cor ?? "var(--tinta-apoio)" }}
    >
      <Icon size={22} strokeWidth={2} />
      {rotulo}
    </button>
  );
}
